import type { Dapp } from '@src/dto';
import { MIN_PREVIEW_IMAGES } from '../constants/form';

/**
 * Fields the Store Listing design marks with "*". Drives the sidebar badge,
 * the Overview "Store Listing" card and the "N required fields left" counter.
 */
export const REQUIRED_LISTING_FIELDS = [
  'dappName',
  'subtitle',
  'description',
  'dappIcon',
  'banner',
  'dappPreviews',
  'languages',
  'appWebsite',
  'supportEmail',
  'licenseUrl',
  'privacyPolicyUrl',
] as const;

export type RequiredListingField = (typeof REQUIRED_LISTING_FIELDS)[number];

export const REQUIRED_LISTING_FIELD_LABELS: Record<
  RequiredListingField,
  string
> = {
  dappName: 'App Name',
  subtitle: 'Subtitle',
  description: 'Description',
  dappIcon: 'dApp Icon',
  banner: 'Banner',
  dappPreviews: 'dApp Preview',
  languages: 'Language',
  appWebsite: 'App Website',
  supportEmail: 'Support Email',
  licenseUrl: 'Terms of Use',
  privacyPolicyUrl: 'Privacy Policy',
};

export interface StoreListingRequiredValues {
  dappName: string;
  subtitle: string;
  description: string;
  hasIcon: boolean;
  hasBanner: boolean;
  previewCount: number;
  languages: string[];
  appWebsite: string;
  supportEmail: string;
  licenseUrl: string;
  privacyPolicyUrl: string;
}

const isBlank = (value: string) => value.trim().length === 0;

export function getMissingRequiredListingFields(
  values: StoreListingRequiredValues,
): RequiredListingField[] {
  const missing: RequiredListingField[] = [];

  if (isBlank(values.dappName)) missing.push('dappName');
  if (isBlank(values.subtitle)) missing.push('subtitle');
  if (isBlank(values.description)) missing.push('description');
  if (!values.hasIcon) missing.push('dappIcon');
  if (!values.hasBanner) missing.push('banner');
  if (values.previewCount < MIN_PREVIEW_IMAGES) missing.push('dappPreviews');
  if (values.languages.length === 0) missing.push('languages');
  if (isBlank(values.appWebsite)) missing.push('appWebsite');
  if (isBlank(values.supportEmail)) missing.push('supportEmail');
  if (isBlank(values.licenseUrl)) missing.push('licenseUrl');
  if (isBlank(values.privacyPolicyUrl)) missing.push('privacyPolicyUrl');

  return missing;
}

export type ListingCompletenessDapp = Pick<
  Dapp,
  | 'dappName'
  | 'subtitle'
  | 'description'
  | 'dappIconUrl'
  | 'bannerUrl'
  | 'dappPreviewUrls'
  | 'languages'
  | 'appWebsite'
  | 'supportEmail'
  | 'licenseUrl'
  | 'privacyPolicyUrl'
>;

export function getListingRequiredValuesFromDapp(
  dapp: ListingCompletenessDapp,
): StoreListingRequiredValues {
  return {
    dappName: dapp.dappName ?? '',
    subtitle: dapp.subtitle ?? '',
    description: dapp.description ?? '',
    hasIcon: Boolean(dapp.dappIconUrl),
    hasBanner: Boolean(dapp.bannerUrl),
    previewCount: dapp.dappPreviewUrls?.length ?? 0,
    languages: dapp.languages ?? [],
    appWebsite: dapp.appWebsite ?? '',
    supportEmail: dapp.supportEmail ?? '',
    licenseUrl: dapp.licenseUrl ?? '',
    privacyPolicyUrl: dapp.privacyPolicyUrl ?? '',
  };
}

/** Missing required fields for a saved dapp record (sidebar/overview badges). */
export function getDappMissingRequiredListingFields(
  dapp: ListingCompletenessDapp,
): RequiredListingField[] {
  return getMissingRequiredListingFields(
    getListingRequiredValuesFromDapp(dapp),
  );
}

export function formatRequiredFieldsLeft(count: number): string {
  return `${count} required ${count === 1 ? 'field' : 'fields'} left`;
}
